import { Link } from 'react-router-dom'
import { Helmet } from 'react-helmet'
import { FiArrowLeft, FiArrowRight, FiCalendar, FiClock } from 'react-icons/fi'
import { blogPosts } from '../data/blogPosts'

const SITE_URL = 'https://presenzia.app'

export default function Blog() {
  const [featured, ...rest] = blogPosts

  const itemListSchema = {
    '@context': 'https://schema.org',
    '@type': 'Blog',
    name: 'Blog de PresenzIA',
    description: 'Artículos sobre control horario, registro de jornada, normativa laboral española y gestión de equipos.',
    url: `${SITE_URL}/blog`,
    blogPost: blogPosts.map((p) => ({
      '@type': 'BlogPosting',
      headline: p.title,
      description: p.description,
      datePublished: p.date,
      url: `${SITE_URL}/blog/${p.slug}`,
      author: { '@type': 'Organization', name: 'PresenzIA' },
    })),
  }

  return (
    <>
      <Helmet>
        <title>Blog | PresenzIA — Control horario, normativa laboral y gestión</title>
        <meta name="description" content="Artículos prácticos sobre registro de jornada, RD-ley 8/2019, sanciones, geolocalización RGPD, teletrabajo, banca de horas y todo lo que necesita saber tu empresa para cumplir la ley." />
        <meta property="og:title" content="Blog de PresenzIA — Control horario y normativa laboral" />
        <meta property="og:description" content="Guías, análisis y recursos sobre control horario en España: normativa, sanciones, herramientas y casos prácticos." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={`${SITE_URL}/blog`} />
        <meta property="og:image" content={`${SITE_URL}/og-image.png`} />
        <meta property="og:locale" content="es_ES" />
        <meta property="og:site_name" content="PresenzIA" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Blog de PresenzIA" />
        <meta name="twitter:description" content="Guías y análisis sobre control horario en España." />
        <meta name="twitter:image" content={`${SITE_URL}/og-image.png`} />
        <meta name="robots" content="index, follow, max-image-preview:large" />
        <link rel="canonical" href={`${SITE_URL}/blog`} />
        <script type="application/ld+json">{JSON.stringify(itemListSchema)}</script>
      </Helmet>

      <div className="min-h-screen bg-white">
        {/* Nav */}
        <nav className="sticky top-0 z-50 bg-white/90 backdrop-blur-xl border-b border-gray-100">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 flex items-center justify-between h-14">
            <Link to="/landing" className="flex items-center gap-2">
              <img src="/logo-presenzia-icon.svg" alt="PresenzIA" className="w-8 h-8 rounded-lg" />
              <span className="text-lg font-bold text-gray-900">Presenz<span className="text-transparent bg-clip-text bg-gradient-to-r from-indigo-500 to-cyan-500">IA</span></span>
            </Link>
            <Link to="/registro" className="text-sm font-semibold text-blue-600 hover:text-blue-700 flex items-center gap-1">
              Prueba gratis <FiArrowRight className="h-3.5 w-3.5" />
            </Link>
          </div>
        </nav>

        <div className="max-w-5xl mx-auto px-4 sm:px-6 pt-6">
          <Link to="/landing" className="inline-flex items-center gap-1 text-sm text-gray-500 hover:text-blue-600 transition-colors">
            <FiArrowLeft className="h-3.5 w-3.5" /> Volver a inicio
          </Link>
        </div>

        {/* Header */}
        <header className="max-w-5xl mx-auto px-4 sm:px-6 pt-8 pb-10">
          <div className="text-sm text-blue-600 font-medium mb-3">Blog</div>
          <h1 className="text-4xl sm:text-5xl font-extrabold text-gray-900 leading-tight mb-4">
            Control horario, sin letra pequeña.
          </h1>
          <p className="text-lg sm:text-xl text-gray-600 max-w-3xl leading-relaxed">
            Análisis, guías y respuestas claras sobre el registro de jornada en España. Lo que debes saber antes de la próxima inspección, contratación o despliegue.
          </p>
        </header>

        {/* Featured post */}
        {featured && (
          <section className="max-w-5xl mx-auto px-4 sm:px-6 mb-12">
            <Link
              to={`/blog/${featured.slug}`}
              className="group block bg-gradient-to-br from-blue-50 via-white to-purple-50 border border-blue-100 rounded-3xl p-8 sm:p-10 hover:shadow-xl transition-all"
            >
              <div className="flex items-center gap-2 text-xs font-semibold text-blue-700 uppercase tracking-wide mb-4">
                <span className="bg-blue-600 text-white px-2.5 py-1 rounded-full">Destacado</span>
                <span>{featured.category}</span>
              </div>
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold text-gray-900 group-hover:text-blue-700 leading-tight mb-3">
                {featured.title}
              </h2>
              <p className="text-base sm:text-lg text-gray-600 mb-5 leading-relaxed max-w-3xl">
                {featured.excerpt}
              </p>
              <div className="flex items-center gap-4 text-sm text-gray-500">
                <span className="inline-flex items-center gap-1"><FiCalendar className="h-3.5 w-3.5" /> {featured.dateDisplay}</span>
                <span className="inline-flex items-center gap-1"><FiClock className="h-3.5 w-3.5" /> {featured.readMinutes} min</span>
                <span className="ml-auto text-blue-600 font-semibold inline-flex items-center gap-1 group-hover:gap-2 transition-all">
                  Leer <FiArrowRight className="h-4 w-4" />
                </span>
              </div>
            </Link>
          </section>
        )}

        {/* Grid */}
        <section className="max-w-5xl mx-auto px-4 sm:px-6 pb-16">
          <h2 className="text-sm font-bold text-gray-900 uppercase tracking-wide mb-5">Todos los artículos</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {rest.map((p) => (
              <Link
                key={p.slug}
                to={`/blog/${p.slug}`}
                className="group block bg-white border border-gray-100 rounded-2xl p-6 hover:border-blue-200 hover:shadow-md transition-all"
              >
                <div className="text-xs font-medium text-blue-600 mb-2">{p.category}</div>
                <h3 className="font-bold text-gray-900 group-hover:text-blue-700 mb-2 leading-snug text-lg">
                  {p.title}
                </h3>
                <p className="text-sm text-gray-500 line-clamp-3 mb-4">{p.excerpt}</p>
                <div className="flex items-center gap-3 text-xs text-gray-400">
                  <span className="inline-flex items-center gap-1"><FiCalendar className="h-3 w-3" /> {p.dateDisplay}</span>
                  <span className="inline-flex items-center gap-1"><FiClock className="h-3 w-3" /> {p.readMinutes} min</span>
                </div>
              </Link>
            ))}
          </div>
        </section>

        {/* CTA */}
        <section className="max-w-5xl mx-auto px-4 sm:px-6 pb-16">
          <div className="bg-gradient-to-br from-blue-600 to-purple-700 rounded-3xl p-10 text-center">
            <h2 className="text-2xl sm:text-3xl font-bold text-white mb-3">¿Ponemos en marcha el control horario de tu empresa?</h2>
            <p className="text-blue-100 mb-6 max-w-2xl mx-auto">
              14 días gratis, sin tarjeta, sin compromiso. Cumple el RD-ley 8/2019 desde hoy.
            </p>
            <Link to="/registro" className="inline-flex items-center gap-2 bg-white text-blue-700 font-bold py-3 px-8 rounded-xl shadow-xl hover:bg-blue-50 transition-all">
              Empezar gratis <FiArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </section>

        {/* Footer */}
        <footer className="bg-gray-50 border-t border-gray-100 py-8">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 text-center">
            <Link to="/landing" className="inline-flex items-center gap-2 mb-4">
              <img src="/logo-presenzia-icon.svg" alt="PresenzIA" className="w-6 h-6 rounded" />
              <span className="text-sm font-bold text-gray-900">Presenz<span className="text-transparent bg-clip-text bg-gradient-to-r from-indigo-500 to-cyan-500">IA</span></span>
            </Link>
            <div className="flex items-center justify-center gap-4 text-xs text-gray-500 mb-3 flex-wrap">
              <Link to="/privacidad" className="hover:text-gray-900">Privacidad</Link>
              <Link to="/aviso-legal" className="hover:text-gray-900">Aviso legal</Link>
              <Link to="/cookies" className="hover:text-gray-900">Cookies</Link>
            </div>
            <p className="text-xs text-gray-400">&copy; {new Date().getFullYear()} PresenzIA. Todos los derechos reservados.</p>
          </div>
        </footer>
      </div>
    </>
  )
}
